import React from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import shallow from 'zustand/shallow'
import {
  alpha,
  Box,
  Button,
  List,
  ListItem,
  Typography,
} from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import useStore from './../store'
import LegendScale from './LegendScale'
import DataControlsPanel from '../DataControlsPanel'
import { getLongPopName, isExplorerView } from '../utils'
import DemoChip from '../DemoChip/DemoChip'
import { OPTIONS_ACTIVE_POINTS } from '../../../../constants/map'
import { ddkLogoPng } from './../../../assets/img'
import { theme } from '../theme'
import GeoChip from '../GeoChip/GeoChip'
import OverlaysPanel from '../OverlaysPanel'

// legend component includes mobile data controls
// TODO: consider breaking into separate components

// Styles for this component.
const useLegendStyles = makeStyles(theme => ({
  root: {
    zIndex: theme.extras.Legend.zIndex,
    backgroundColor: theme.palette.background.paper,

    // MOBILE DATA CONTROL STYLES
    position: 'fixed',
    '& .level-scale-parent': {
      marginTop: theme.spacing(1.5),
    },
    // transition:
    //   'width 300ms ease-in-out, max-height 300ms ease-in-out',
    width: '100vw', //620px
    // Adjust for different app bar height.
    top: '55px',
    right: '0px',
    cursor: 'default',
    overflow: 'hidden',

    '& .demo-icon': {
      marginLeft: theme.spacing(1),
    },

    // NON-MOBILE LEGEND
    [theme.breakpoints.up('sm')]: {
      boxShadow: theme.shadows[3],
      width: 317,
      position: 'absolute',
      right: theme.extras.Legend.cushionRight,
      top: 80,
      borderRadius: 5,

      '& .geo-icon': {
        marginRight: 0,
        marginLeft: theme.spacing(0.5),
      },
    },
  },

  // SHARED STYLES
  controller: {
    boxSizing: 'border-box',
    background: theme.palette.background.paper,
    maxHeight: `calc(100vh - ${theme.extras.variables.dimensions.navbarHeightSmPx}px)`,
    overflowY: 'auto',
    [theme.breakpoints.up('sm')]: {
      borderRadius: 5,
    },
  },
  section: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
      // borderRadius: 5,
    },

    borderTop: theme.extras.styles.faintBorder,
    // '&.demos': {
    '& ul': {
      padding: 0,
      // padding: theme.spacing(0.5, 0, 0, 0),
    },
    '& li:not(.MuiListItem-dense)': {
      padding: theme.spacing(0.5, 0, 0, 0),
    },
    // },
  },
  oneLinerTitle: {
    flexShrink: 0,
    paddingRight: theme.spacing(0.75),
    lineHeight: 1.7,
  },

  // MOBILE DATA CONTROL STYLES
  mobileControls: {
    width: '100%',
    '& .panel-header': {
      display: 'none',
    },
    '& .MuiToggleButtonGroup-root .MuiToggleButton-root': {
      lineHeight: 1,
    },
    '& .control-section': {
      padding: theme.spacing(1, 0, 1.5),
      border: 'none',
      '&.main': {
        border: 'none',
      },
    },
  },
  controlGuts: {
    transition: 'height 300ms ease-in-out',
    height: 0,
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      height: theme.extras.Legend.height, // '330px',
      // overflow: 'visible',
    },
    '&.controlsOpen': {
      // height: theme.extras.Legend.heightMobile,
      overflowY: 'auto',
      height: '100%',
      [theme.breakpoints.up('sm')]: {
        height: theme.extras.Legend.height,
        overflow: 'visible',
      },
    },
  },
  controlIcon: {
    transition: 'transform 300ms ease-in-out',
    transform: 'rotate(180deg)',
  },
  controlButton: {
    textAlign: 'center',
    '&.controlsOpen': {
      // fixes bug where button is obscured by mobile browser url bar
      marginBottom: 110,
      '& $controlIcon': {
        transform: 'rotate(0deg)',
      },
    },
  },
  controlBtnLabel: {
    color: theme.extras.variables.colors.ddkMainBlue,
    fontSize: '14px',
    letterSpacing: '1.25px',
    verticalAlign: 'middle',
  },

  embed: {
    top: theme.spacing(2),
  },
}))

const Legend = ({ ...props }) => {
  const {
    activeYear,
    activeGeoType,
    activeRatingType,
    hoveredFeature,
    activeNorm,
    activePointLayers,
    showDensityOverlays,
    showZipBoundaries,
    redliningActive,
    activeMetric,
    legendControlActive,
    slideoutPanel,
    activeView,
    breakpoint,
    setStoreValues,
  } = useStore(
    state => ({
      activeYear: state.activeYear,
      activeGeoType: state.activeGeoType,
      activeRatingType: state.activeRatingType,
      hoveredFeature: state.hoveredFeature,
      activeNorm: state.activeNorm,
      activePointLayers: state.activePointLayers,
      showDensityOverlays: state.showDensityOverlays,
      showZipBoundaries: state.showZipBoundaries,
      redliningActive: state.redliningActive,
      activeMetric: state.activeMetric,
      legendControlActive: state.legendControlActive,
      slideoutPanel: state.slideoutPanel,
      activeView: state.activeView,
      breakpoint: state.breakpoint,
      setStoreValues: state.setStoreValues,
    }),
    shallow,
  )

  const key = `${activeRatingType}${activeMetric}${activeNorm}`
  const hoveredRating = hoveredFeature?.properties[key]
  const openControlHandler = () =>
    setStoreValues({
      slideoutPanel: {
        ...slideoutPanel,
        active: true,
        panel: 'control',
      },
    })

  const toggleControls = () =>
    setStoreValues({
      legendControlActive: !legendControlActive,
    })

  const classes = useLegendStyles()
  const controlPanelOpen =
    slideoutPanel.active &&
    slideoutPanel.panel === 'control'

  const getDemoItems = () => {
    if (!showDensityOverlays || !activePointLayers.length)
      return null
    return (
      <ListItem style={{ display: 'block' }}>
        <Typography variant="body1">
          {i18n.translate(`LEGEND_DEMO`)}
        </Typography>
        <List>
          {OPTIONS_ACTIVE_POINTS.options
            .slice()
            .reverse()
            .filter(demo =>
              activePointLayers.includes(demo),
            )
            .map(demo => (
              <ListItem key={demo}>
                <DemoChip
                  nameVariant="body2"
                  demo={demo}
                  nameFormatter={getLongPopName}
                />
              </ListItem>
            ))}
        </List>
      </ListItem>
    )
  }

  const showRedlining =
    activeGeoType === 'tracts' && redliningActive
  const getRedliningItem = () => {
    if (!showRedlining) return null
    const redliningTypes = Object.keys(
      theme.extras.redlining,
    )
    return (
      <ListItem disableGutters>
        <Typography
          className={classes.oneLinerTitle}
          variant="body1"
        >
          {i18n.translate(`LEGEND_REDLINING`)}
        </Typography>
        <List
          disablePadding={true}
          style={{
            display: 'inline-flex',
            flexGrow: 1,
          }}
        >
          {redliningTypes.map(type => (
            <ListItem
              key={type}
              disableGutters={true}
              dense={true}
              style={{
                // padding: '0 4px 0 6px',
                '& .geoChip': {
                  flexBasis: 40,
                },
              }}
            >
              <GeoChip
                id={type}
                innerText={type}
                border={theme.extras.redlining[type]}
                width="100%"
                height={14}
                fill={alpha(
                  theme.extras.redlining[type],
                  0.15,
                )}
              />
            </ListItem>
          ))}
        </List>
      </ListItem>
    )
  }

  const getZipItem = () => {
    if (!showZipBoundaries) return null
    return (
      <ListItem disableGutters>
        <Typography
          className={classes.oneLinerTitle}
          variant="body1"
        >
          {i18n.translate('LEGEND_ZIP_OVERLAY')}
        </Typography>
        <GeoChip
          width="25px"
          border={theme.extras.variables.colors.zipYellow}
        />
      </ListItem>
    )
  }

  const demoItems = getDemoItems()
  const redliningItem = getRedliningItem()
  const zipItem = getZipItem()
  return (
    <>
      {/* DESKTOP LEGEND */}
      {breakpoint != 'xs' && isExplorerView(activeView) && (
        <Box className={clsx('map-legend', classes.root)}>
          <Box className={clsx(classes.section)}>
            {/* logo to display only in screenshot */}
            <img
              src={ddkLogoPng}
              className="screenshot-reveal"
              style={{
                display: 'none',
                height: '34px',
                margin: '8px 0 12px',
              }}
            />
            <LegendScale
              controlPanelOpen={controlPanelOpen}
              activeGeoType={activeGeoType}
              activeRatingType={activeRatingType}
              activeYear={activeYear}
              activeMetric={activeMetric}
              activeNorm={activeNorm}
              hoveredRating={hoveredRating}
              openControlHandler={openControlHandler}
            />
          </Box>
          {(demoItems || redliningItem || zipItem) && (
            <Box className={clsx(classes.section)}>
              <Typography
                variant="h2"
                style={{ marginBottom: 4 }}
              >
                {i18n.translate(`CONTROL_PANEL_OVERLAYS`)}
              </Typography>
              <List>
                {demoItems}
                {redliningItem}
                {zipItem}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* MOBILE LEGEND/CONTROLS */}
      {breakpoint === 'xs' && isExplorerView(activeView) && (
        <Box className={clsx('map-legend', classes.root)}>
          <Box
            className={clsx(
              classes.controller,
              classes.section,
            )}
          >
            <LegendScale
              activeRatingType={activeRatingType}
              activeGeoType={activeGeoType}
              activeYear={activeYear}
              activeMetric={activeMetric}
              activeNorm={activeNorm}
              headingless={true}
            />
            <Box
              className={clsx(classes.controlGuts, {
                controlsOpen: !!legendControlActive,
              })}
            >
              <DataControlsPanel
                className={classes.mobileControls}
              />
              <Typography
                variant="h1"
                style={{ paddingBottom: 8 }}
              >
                {i18n.translate(`CONTROL_PANEL_OVERLAYS`)}
              </Typography>
              <OverlaysPanel
                className={classes.mobileControls}
              />
            </Box>
            <Box
              className={clsx(classes.controlButton, {
                controlsOpen: !!legendControlActive,
              })}
            >
              <Button
                onClick={toggleControls}
                classes={{
                  label: classes.controlBtnLabel,
                }}
              >
                <ExpandLessIcon
                  className={classes.controlIcon}
                />
                <span>
                  {i18n.translate(
                    `LEGEND_CONTROL_${legendControlActive
                      .toString()
                      .toUpperCase()}`,
                  )}
                </span>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* EMBED LEGEND */}
      {activeView === 'embed' && (
        <Box
          className={clsx(
            'map-legend',
            classes.root,
            classes.embed,
          )}
        >
          <Box
            className={clsx(
              classes.controller,
              classes.section,
            )}
          >
            <LegendScale
              activeRatingType={activeRatingType}
              activeGeoType={activeGeoType}
              activeYear={activeYear}
              activeMetric={activeMetric}
              activeNorm={activeNorm}
            />
          </Box>
        </Box>
      )}
    </>
  )
}

Legend.propTypes = {}

Legend.defaultProps = {}

export default Legend
